var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { DI } from '@aurelia/kernel';
import { IHttpClient, json } from '@aurelia/fetch-client';
import { extend, forEach, isFunction, isString, joinUrl, camelCase, status, } from './auth-utilities';
import { IStorage } from './storage';
import { IPopup } from './popup';
import { IAuthOptions } from './configuration';
import { IAuthentication } from './authentication';
export const IOAuth2 = DI.createInterface("IOAuth2", x => x.singleton(OAuth2));
let OAuth2 = class OAuth2 {
    constructor(storage, popup, auth, http, config) {
        this.storage = storage;
        this.popup = popup;
        this.auth = auth;
        this.http = http;
        this.config = config;
        this.defaults = {
            url: null,
            name: null,
            state: null,
            scope: null,
            scopeDelimiter: null,
            redirectUri: null,
            popupOptions: null,
            authorizationEndpoint: null,
            responseParams: null,
            requiredUrlParams: null,
            optionalUrlParams: null,
            defaultUrlParams: ['response_type', 'client_id', 'redirect_uri'],
            responseType: 'code',
        };
    }
    open(options, userData) {
        // @ts-expect-error
        let current = extend({}, this.defaults, options);
        //state handling
        let stateName = current.name + '_state';
        if (isFunction(current.state)) {
            this.storage.set(stateName, current.state());
        }
        else if (isString(current.state)) {
            this.storage.set(stateName, current.state);
        }
        //nonce handling
        let nonceName = current.name + '_nonce';
        if (isFunction(current.nonce)) {
            this.storage.set(nonceName, current.nonce());
        }
        else if (isString(current.nonce)) {
            this.storage.set(nonceName, current.nonce);
        }
        let url = current.authorizationEndpoint + '?' + this.buildQueryString(current);
        let openPopup;
        if (this.config.platform === 'mobile') {
            openPopup = this.popup
                .open(url, current.name, current.popupOptions, current.redirectUri)
                .eventListener(current.redirectUri);
        }
        else {
            openPopup = this.popup
                .open(url, current.name, current.popupOptions, current.redirectUri)
                .pollPopup();
        }
        return openPopup.then((oauthData) => {
            if (oauthData.state && oauthData.state !== this.storage.get(stateName)) {
                return Promise.reject('OAuth 2.0 state parameter mismatch.');
            }
            if (current.responseType.toUpperCase().indexOf('TOKEN') !== -1) {
                //meaning implicit flow or hybrid flow
                if (!this.verifyIdToken(oauthData, current.name)) {
                    return Promise.reject('OAuth 2.0 Nonce parameter mismatch.');
                }
                return oauthData;
            }
            return this.exchangeForToken(oauthData, userData, current); //responseType is authorization code only (no token nor id_token)
        });
    }
    verifyIdToken(oauthData, providerName) {
        let idToken = oauthData && oauthData[this.config.responseIdTokenProp];
        if (!idToken)
            return true;
        let idTokenObject = this.auth.decomposeToken(idToken);
        if (!idTokenObject)
            return true;
        let nonceFromToken = idTokenObject.nonce;
        if (!nonceFromToken)
            return true;
        let nonceInStorage = this.storage.get(providerName + '_nonce');
        if (nonceFromToken !== nonceInStorage) {
            return false;
        }
        return true;
    }
    exchangeForToken(oauthData, userData, current) {
        // @ts-expect-error
        let data = extend({}, userData, {
            code: oauthData.code,
            clientId: current.clientId,
            redirectUri: current.redirectUri,
        });
        if (oauthData.state) {
            data.state = oauthData.state;
        }
        // @ts-expect-error
        forEach(current.responseParams, (param) => (data[param] = oauthData[param]));
        let exchangeForTokenUrl = this.config.baseUrl
            ? joinUrl(this.config.baseUrl, current.url)
            : current.url;
        let credentials = this.config.withCredentials
            ? 'include'
            : 'same-origin';
        return this.http
            .fetch(exchangeForTokenUrl, {
            method: 'post',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: json(data),
            credentials: credentials,
        })
            .then(status);
    }
    buildQueryString(current) {
        let keyValuePairs = [];
        let urlParams = [
            'defaultUrlParams',
            'requiredUrlParams',
            'optionalUrlParams',
        ];
        // @ts-expect-error
        forEach(urlParams, (params) => {
            // @ts-expect-error
            forEach(current[params], (paramName) => {
                let camelizedName = camelCase(paramName);
                let paramValue = isFunction(current[paramName])
                    ? current[paramName]()
                    : current[camelizedName];
                if (paramName === 'state') {
                    let stateName = current.name + '_state';
                    paramValue = encodeURIComponent(this.storage.get(stateName));
                }
                if (paramName === 'nonce') {
                    let nonceName = current.name + '_nonce';
                    paramValue = encodeURIComponent(this.storage.get(nonceName));
                }
                if (paramName === 'scope' && Array.isArray(paramValue)) {
                    paramValue = paramValue.join(current.scopeDelimiter);
                    if (current.scopePrefix) {
                        paramValue = [current.scopePrefix, paramValue].join(current.scopeDelimiter);
                    }
                }
                keyValuePairs.push([paramName, paramValue]);
            });
        });
        return keyValuePairs.map((pair) => pair.join('=')).join('&');
    }
};
OAuth2 = __decorate([
    __param(0, IStorage),
    __param(1, IPopup),
    __param(2, IAuthentication),
    __param(3, IHttpClient),
    __param(4, IAuthOptions),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], OAuth2);
export { OAuth2 };
//# sourceMappingURL=oAuth2.js.map